<template>
  <button
    v-show="visible"
    :class="[
      getThemeDisplay,
      {
        'pointer-events-none': isDisabled,
        'ds-button-icon': icon,
        'ds-button-outline': outline,
        'ds-button-tooltip': hasTooltip,
      },
      `${getThemeDisplay}-${themeColor}`,
      getButtonSize,
      getButtonStyle,
      getButtonOpacity,
      getClassDisabled,
    ]"
    :type="type"
    :disabled="isDisabled"
    data-testid="ds-button"
    @click="$emit('click', $event)"
  >
    <span v-if="loading" class="ds-button-loader">
      <DsLoader :theme-color="getLoaderThemeColor" />
      <span
        v-if="!hideLoadingText"
        class="ds-button-loader-text"
        data-testid="ds-button-loader-text"
      >
        {{ loadingLabel }}
      </span>
    </span>
    <template v-else>
      <DsTooltip v-if="hasTooltip" :content="tooltip" portal :close-delay="0">
        <template #activator>
          <slot />
        </template>
      </DsTooltip>
      <slot v-else />
    </template>
  </button>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { PropType } from "vue";
import { useI18n } from "vue-i18n";

import DsLoader from "../loader/DsLoader.vue";
import DsTooltip from "../tooltip/DsTooltip.vue";

import type {
  DsButtonType,
  DsDisplayType,
  DsOpacity,
  DsThemeColor,
  DsThemeSize,
} from "../../types";

defineOptions({
  name: "DsButton",
});

defineEmits<{
  click: [Event];
}>();

const props = defineProps({
  type: {
    type: String as PropType<DsButtonType>,
    default: "button",
  },
  visible: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingLabel: {
    type: String,
    default: () => {
      const { t } = useI18n();

      return t("ds.component.button.loading");
    },
  },
  //@todo rename to hideLoadingLabel
  hideLoadingText: {
    type: Boolean,
    default: false,
  },
  themeDisplay: {
    type: String as PropType<DsDisplayType>,
    required: false,
    default: "button",
  },
  themeColor: {
    type: String as PropType<DsThemeColor>,
    required: false,
    default: "black",
  },
  outline: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String as PropType<DsThemeSize>,
    required: false,
    default: "md",
  },
  icon: {
    type: Boolean,
    required: false,
    default: false,
  },
  pill: {
    type: Boolean,
    default: true,
  },
  opacity: {
    type: String as PropType<DsOpacity>,
    default: "100",
  },
  tooltip: {
    type: String,
    default: "",
  },
});

const isDisabled = computed((): boolean => {
  return props.disabled || props.loading;
});

const getThemeDisplay = computed((): string =>
  props.themeDisplay === "button" ? "ds-button" : "ds-link"
);

const getLoaderThemeColor = computed((): DsThemeColor => {
  return props.outline && ["black", "yellow"].includes(props.themeColor)
    ? "black"
    : "white";
});

const getButtonSize = computed((): string =>
  props.themeDisplay === "button" ? `ds-button-${props.size}` : ""
);

const getButtonStyle = computed((): string =>
  props.themeDisplay === "button" && props.pill ? "ds-button-pill" : ""
);

const getButtonOpacity = computed((): string => {
  return `ds-button-opacity-${props.opacity}`;
});

const getClassDisabled = computed((): string => {
  return isDisabled.value ? `ds-${props.themeDisplay}-disabled` : "";
});

const hasTooltip = computed((): boolean => props.tooltip !== "");
</script>

<style lang="scss" scoped>
.ds-button-tooltip {
  &.ds-button {
    &-xxs {
      @apply p-0;
      :deep(.ds-tooltip-activator),
      .ds-button-loader {
        @apply py-1 px-2 text-xs leading-[18px];
      }

      &.ds-button-icon {
        :deep(.ds-tooltip-activator),
        .ds-button-loader {
          @apply p-[5px];
        }
      }
    }
    &-xs {
      @apply p-0;
      :deep(.ds-tooltip-activator),
      .ds-button-loader {
        @apply py-2 px-3;
      }

      &.ds-button-icon {
        :deep(.ds-tooltip-activator),
        .ds-button-loader {
          @apply p-[9px];
        }
      }
    }
    &-sm {
      @apply p-0;
      :deep(.ds-tooltip-activator),
      .ds-button-loader {
        @apply py-2 px-3;
      }

      &.ds-button-icon {
        :deep(.ds-tooltip-activator),
        .ds-button-loader {
          @apply p-[9px];
        }
      }
    }
    &-md {
      @apply p-0;
      :deep(.ds-tooltip-activator),
      .ds-button-loader {
        @apply py-2.5 px-5;
      }

      &.ds-button-icon {
        :deep(.ds-tooltip-activator),
        .ds-button-loader {
          @apply p-2.5;
        }
      }
    }
    &-lg {
      @apply p-0;
      :deep(.ds-tooltip-activator),
      .ds-button-loader {
        @apply py-2.5 px-5;
      }

      &.ds-button-icon {
        :deep(.ds-tooltip-activator),
        .ds-button-loader {
          @apply p-3;
        }
      }
    }
    &-xl {
      @apply p-0;
      :deep(.ds-tooltip-activator),
      .ds-button-loader {
        @apply py-3.5 px-6;
      }

      &.ds-button-icon {
        :deep(.ds-tooltip-activator),
        .ds-button-loader {
          @apply p-4;
        }
      }
    }
    &-xxl {
      @apply p-0;
      :deep(.ds-tooltip-activator),
      .ds-button-loader {
        @apply py-4 px-8;
      }

      &.ds-button-icon {
        :deep(.ds-tooltip-activator),
        .ds-button-loader {
          @apply p-[18px];
        }
      }
    }
  }
}
</style>
